import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Button,
  Card,
  EmptyState,
  Input,
  LoadingState,
  SectionHeader,
  StatusChip,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createCreatorsApi } from "../api";
import { creatorTone, formatLabel } from "../labels";
import type {
  ApplicationDecisionDto,
  BrandDealDto,
  CampaignDto,
  CreatorApplicationDto,
  CreatorPaymentDto,
  CreatorProfileDto,
  CreatorProgramDto,
  InvitationDto,
  MembershipDto,
  ParticipationDto,
  PerformanceDto,
  ReviewNoteDto,
  TierHistoryDto,
} from "../types";
import {
  AttachFileDialog,
  DecideApplicationDialog,
  LinkPayoutDialog,
  ReasonApprovalDialog,
  ReviewNoteDialog,
} from "../components/CreatorDialogs";

function DetailShell({
  crumb,
  crumbTo,
  title,
  description,
  status,
  loading,
  error,
  onRetry,
  actions,
  children,
}: {
  crumb: string;
  crumbTo: string;
  title: string;
  description?: string | undefined;
  status?: string | undefined;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  actions?: ReactNode;
  children: ReactNode;
}) {
  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Creator Center", to: "/app/creators" },
    { label: crumb, to: crumbTo },
    { label: title },
  ]);

  if (loading) return <LoadingState message="Loading…" />;
  if (error) {
    return (
      <EmptyState
        title="Record unavailable"
        description={error}
        reason="scope"
        action={
          <Button variant="secondary" onClick={onRetry}>
            Retry
          </Button>
        }
      />
    );
  }

  return (
    <Stack gap={24}>
      <SectionHeader
        title={title}
        {...(description ? { description } : {})}
        {...(actions ? { actions } : {})}
      />
      {status ? (
        <StatusChip tone={creatorTone(status)}>{formatLabel(status)}</StatusChip>
      ) : null}
      {children}
    </Stack>
  );
}

function Lines({ items }: { items: { label: string; value: ReactNode }[] }) {
  return (
    <Card>
      <Stack gap={8}>
        {items.map((item) => (
          <Text key={item.label} role="body" as="p">
            {item.label}: {item.value}
          </Text>
        ))}
      </Stack>
    </Card>
  );
}

function RelatedLink({ to, label }: { to: string; label: string }) {
  const navigate = useNavigate();
  return (
    <Button size="sm" variant="secondary" onClick={() => void navigate(to)}>
      {label}
    </Button>
  );
}

export function ProfileDetailPage() {
  const { profileId = "" } = useParams();
  const client = useApiClient();
  const api = useMemo(() => createCreatorsApi(client), [client]);
  const toast = useToast();
  const [row, setRow] = useState<CreatorProfileDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionOpen, setActionOpen] = useState(false);
  const [fileOpen, setFileOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRow((await api.getProfile(profileId)).creator_profile);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [api, profileId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <DetailShell
        crumb="Creator Profiles"
        crumbTo="/app/creators/profiles"
        title={row?.id ?? "Creator Profile"}
        description="User 1:1 extension. current_tier is summary; open Tier History for the authoritative record."
        status={row?.status}
        loading={loading}
        error={error}
        onRetry={() => void load()}
        actions={
          <Flex gap={8}>
            <Button variant="secondary" onClick={() => setFileOpen(true)}>
              Attach file
            </Button>
            <Button variant="secondary" onClick={() => setActionOpen(true)}>
              Status actions
            </Button>
          </Flex>
        }
      >
        {row ? (
          <>
            <Lines
              items={[
                { label: "User", value: row.user_id },
                { label: "Current tier (summary)", value: row.current_tier ? formatLabel(row.current_tier) : "—" },
                { label: "Market", value: row.market_id ?? "—" },
                { label: "Account owner", value: row.account_owner_user_id ?? "—" },
              ]}
            />
            <Flex gap={8} wrap>
              <RelatedLink to={`/app/users/${row.user_id}`} label="Open User" />
              <RelatedLink
                to={`/app/creators/applications?creator_profile_id=${row.id}`}
                label="Applications"
              />
              <RelatedLink
                to={`/app/creators/tier-history?creator_profile_id=${row.id}`}
                label="Tier history"
              />
              <RelatedLink
                to={`/app/creators/memberships?creator_profile_id=${row.id}`}
                label="Memberships"
              />
              <RelatedLink
                to={`/app/creators/payments?creator_profile_id=${row.id}`}
                label="Payments"
              />
            </Flex>
          </>
        ) : null}
      </DetailShell>
      <ReasonApprovalDialog
        open={actionOpen}
        title="Profile action"
        notice="Restore from suspended needs Dual Approval."
        actions={["activate", "restrict", "suspend", "deactivate", "restore"]}
        onClose={() => {
          setActionOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.applyProfileAction(profileId, input.action as never, {
              ...(input.reason ? { reason: input.reason } : {}),
              ...(input.approval_id ? { approval_id: input.approval_id } : {}),
            });
            toast.push({ title: "Profile updated", tone: "success" });
            setActionOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
      <AttachFileDialog
        open={fileOpen}
        busy={busy}
        error={actionError}
        onClose={() => {
          setFileOpen(false);
          setActionError(null);
        }}
        onSubmit={async (body) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.attachProfileFile(profileId, body);
            toast.push({ title: "File attached", tone: "success" });
            setFileOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}

/**
 * What the applicant wrote in Play (lib/creatorApplications.ts review_context):
 * name, birthday, and their links on other apps (Play Polishing 18.3). Links
 * are what they typed; only http(s) ones are clickable, and they open in a new
 * tab with no referrer.
 */
function ApplicantDetails({ context }: { context: Record<string, unknown> | null | undefined }) {
  const str = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : "—");
  const links = Array.isArray(context?.links) ? (context!.links as unknown[]).filter((l): l is string => typeof l === "string" && l.trim() !== "") : [];
  return (
    <>
      <Text role="h2" as="h2">
        Applicant
      </Text>
      <Lines
        items={[
          { label: "Legal name", value: str(context?.legal_name) },
          { label: "Birthdate", value: str(context?.birthdate) },
        ]}
      />
      <Text role="title" as="h3">
        Links on other apps
      </Text>
      {links.length === 0 ? (
        <EmptyState title="No links" description="This application has no links." reason="none" />
      ) : (
        <ul>
          {links.map((link, i) => {
            const href = /^https?:\/\//i.test(link) ? link : /^[\w-]+(\.[\w-]+)+(\/|$)/.test(link) ? `https://${link}` : null;
            return (
              <li key={`${i}-${link}`}>
                {href ? (
                  <a href={href} target="_blank" rel="noopener noreferrer">{link}</a>
                ) : (
                  link
                )}
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

export function ApplicationDetailPage() {
  const { applicationId = "" } = useParams();
  const client = useApiClient();
  const api = useMemo(() => createCreatorsApi(client), [client]);
  const toast = useToast();
  const [row, setRow] = useState<CreatorApplicationDto | null>(null);
  const [notes, setNotes] = useState<ReviewNoteDto[]>([]);
  const [decisions, setDecisions] = useState<ApplicationDecisionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionOpen, setActionOpen] = useState(false);
  const [decideOpen, setDecideOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [fileOpen, setFileOpen] = useState(false);
  const [reviewerId, setReviewerId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [detail, noteList, decisionList] = await Promise.all([
        api.getApplication(applicationId),
        api.listReviewNotes(applicationId),
        api.listDecisions(applicationId),
      ]);
      setRow(detail.creator_application);
      setNotes(noteList.review_notes);
      setDecisions(decisionList.decisions);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [api, applicationId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <DetailShell
        crumb="Applications"
        crumbTo="/app/creators/applications"
        title={row?.id ?? "Application"}
        status={row?.status}
        loading={loading}
        error={error}
        onRetry={() => void load()}
        actions={
          <Flex gap={8} wrap>
            <Button variant="secondary" onClick={() => setNoteOpen(true)}>
              Add note
            </Button>
            <Button variant="secondary" onClick={() => setFileOpen(true)}>
              Attach file
            </Button>
            <Button variant="secondary" onClick={() => setActionOpen(true)}>
              Workflow
            </Button>
            <Button variant="primary" onClick={() => setDecideOpen(true)}>
              Record decision
            </Button>
          </Flex>
        }
      >
        {row ? (
          <>
            <Lines
              items={[
                { label: "Type", value: formatLabel(row.application_type) },
                { label: "Profile", value: row.creator_profile_id },
                { label: "Reviewer", value: row.assigned_reviewer_user_id ?? "—" },
                { label: "Submitted", value: row.submitted_at ?? "—" },
                { label: "Decision reason", value: row.decision_reason ?? "—" },
              ]}
            />
            <RelatedLink
              to={`/app/creators/profiles/${row.creator_profile_id}`}
              label="Open profile"
            />
            <ApplicantDetails context={row.review_context} />
            <Text role="h2" as="h2">
              Reviewer notes
            </Text>
            {notes.length === 0 ? (
              <EmptyState title="No notes" reason="none" />
            ) : (
              <Table>
                <Thead>
                  <Tr>
                    <Th>When</Th>
                    <Th>Author</Th>
                    <Th>Class</Th>
                    <Th>Note</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {notes.map((note) => (
                    <Tr key={note.id}>
                      <Td>{note.created_at}</Td>
                      <Td>{note.author_user_id}</Td>
                      <Td>{formatLabel(note.classification)}</Td>
                      <Td>{note.note_reference}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
            <Text role="h2" as="h2">
              Decisions
            </Text>
            {decisions.length === 0 ? (
              <EmptyState title="No decisions" reason="none" />
            ) : (
              <Table>
                <Thead>
                  <Tr>
                    <Th>When</Th>
                    <Th>Decision</Th>
                    <Th>By</Th>
                    <Th>Reason</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {decisions.map((item) => (
                    <Tr key={item.id}>
                      <Td>{item.decided_at}</Td>
                      <Td>{formatLabel(item.decision)}</Td>
                      <Td>{item.decided_by_user_id}</Td>
                      <Td>{item.decision_reason ?? "—"}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </>
        ) : null}
      </DetailShell>
      <ReasonApprovalDialog
        open={actionOpen}
        title="Application workflow"
        notice="start_review can assign a reviewer."
        actions={["submit", "start_review", "resume", "close"]}
        extraFields={
          <Input
            label="Assigned reviewer User ID (start_review)"
            value={reviewerId}
            onChange={(event) => setReviewerId(event.target.value)}
          />
        }
        onClose={() => {
          setActionOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            const body = {
              ...(input.reason ? { reason: input.reason } : {}),
              ...(reviewerId.trim()
                ? { assigned_reviewer_user_id: reviewerId.trim() }
                : {}),
            };
            if (input.action === "submit") await api.submitApplication(applicationId, body);
            else if (input.action === "start_review") await api.startReview(applicationId, body);
            else if (input.action === "resume") await api.resumeApplication(applicationId, body);
            else await api.closeApplication(applicationId, body);
            toast.push({ title: "Application updated", tone: "success" });
            setActionOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
      <DecideApplicationDialog
        open={decideOpen}
        busy={busy}
        error={actionError}
        onClose={() => {
          setDecideOpen(false);
          setActionError(null);
        }}
        onSubmit={async (body) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.decideApplication(applicationId, body);
            toast.push({ title: "Decision recorded", tone: "success" });
            setDecideOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
      <ReviewNoteDialog
        open={noteOpen}
        busy={busy}
        error={actionError}
        onClose={() => {
          setNoteOpen(false);
          setActionError(null);
        }}
        onSubmit={async (body) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.addReviewNote(applicationId, body);
            toast.push({ title: "Note added", tone: "success" });
            setNoteOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
      <AttachFileDialog
        open={fileOpen}
        busy={busy}
        error={actionError}
        onClose={() => {
          setFileOpen(false);
          setActionError(null);
        }}
        onSubmit={async (body) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.attachApplicationFile(applicationId, body);
            toast.push({ title: "File attached", tone: "success" });
            setFileOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}

function useListBackedRow<T extends { id: string }>(stateRow: T | null) {
  const [row, setRow] = useState<T | null>(stateRow);
  useEffect(() => {
    setRow(stateRow);
  }, [stateRow]);
  return [row, setRow] as const;
}

export function TierHistoryDetailPage() {
  const { tierHistoryId = "" } = useParams();
  const location = useLocation();
  const client = useApiClient();
  const api = useMemo(() => createCreatorsApi(client), [client]);
  const toast = useToast();
  const [row, setRow] = useListBackedRow(
    (location.state as TierHistoryDto | null)?.id === tierHistoryId
      ? (location.state as TierHistoryDto)
      : null,
  );
  const [actionOpen, setActionOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  return (
    <>
      <DetailShell
        crumb="Tier History"
        crumbTo="/app/creators/tier-history"
        title={row?.id ?? tierHistoryId}
        description="No item GET on this resource. Reopen from the queue after a full refresh."
        status={row?.status}
        loading={false}
        error={row ? null : "Open this row from the Tier History queue. The API has no item GET."}
        onRetry={() => undefined}
        actions={
          <Button variant="secondary" onClick={() => setActionOpen(true)}>
            Status actions
          </Button>
        }
      >
        {row ? (
          <Lines
            items={[
              { label: "Tier", value: formatLabel(row.tier) },
              { label: "Profile", value: row.creator_profile_id },
              { label: "Effective from", value: row.effective_from },
              { label: "Effective to", value: row.effective_to ?? "—" },
              { label: "Reason", value: row.reason ?? "—" },
              { label: "Source application", value: row.source_application_id ?? "—" },
            ]}
          />
        ) : null}
      </DetailShell>
      <ReasonApprovalDialog
        open={actionOpen}
        title="Tier action"
        notice="Override and restore-from-suspended need Dual Approval. Activate closes the prior active row."
        actions={["activate", "conditional_activate", "suspend", "revoke", "expire"]}
        onClose={() => {
          setActionOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.applyTierAction(tierHistoryId, input.action as never, {
              ...(input.reason ? { reason: input.reason } : {}),
              ...(input.approval_id ? { approval_id: input.approval_id } : {}),
            });
            toast.push({ title: "Tier history updated", tone: "success" });
            setActionOpen(false);
            setRow(row);
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}

export function ProgramDetailPage() {
  const { programId = "" } = useParams();
  const client = useApiClient();
  const api = useMemo(() => createCreatorsApi(client), [client]);
  const toast = useToast();
  const [row, setRow] = useState<CreatorProgramDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionOpen, setActionOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRow((await api.getProgram(programId)).creator_program);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [api, programId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <DetailShell
        crumb="Programs"
        crumbTo="/app/creators/programs"
        title={row?.name ?? "Program"}
        status={row?.status}
        loading={loading}
        error={error}
        onRetry={() => void load()}
        actions={
          <Button variant="secondary" onClick={() => setActionOpen(true)}>
            Status actions
          </Button>
        }
      >
        {row ? (
          <>
            <Lines
              items={[
                { label: "Owner", value: row.owner_user_id ?? "—" },
                { label: "Department", value: formatLabel(row.owning_department) },
                { label: "Review cadence", value: row.review_cadence ?? "—" },
                { label: "Market", value: row.market_id ?? "—" },
              ]}
            />
            <Flex gap={8}>
              <RelatedLink
                to={`/app/creators/memberships?creator_program_id=${row.id}`}
                label="Memberships"
              />
              <RelatedLink
                to={`/app/creators/campaigns?creator_program_id=${row.id}`}
                label="Campaigns"
              />
            </Flex>
          </>
        ) : null}
      </DetailShell>
      <ReasonApprovalDialog
        open={actionOpen}
        title="Program action"
        actions={["approve", "activate", "pause", "resume", "retire"]}
        onClose={() => {
          setActionOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.applyProgramAction(programId, input.action as never, {
              ...(input.reason ? { reason: input.reason } : {}),
            });
            toast.push({ title: "Program updated", tone: "success" });
            setActionOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}

export function MembershipDetailPage() {
  const { membershipId = "" } = useParams();
  const location = useLocation();
  const client = useApiClient();
  const api = useMemo(() => createCreatorsApi(client), [client]);
  const toast = useToast();
  const [row] = useListBackedRow(
    (location.state as MembershipDto | null)?.id === membershipId
      ? (location.state as MembershipDto)
      : null,
  );
  const [actionOpen, setActionOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  return (
    <>
      <DetailShell
        crumb="Memberships"
        crumbTo="/app/creators/memberships"
        title={row?.id ?? membershipId}
        description="No item GET. Reopen from the queue after a full refresh."
        status={row?.status}
        loading={false}
        error={row ? null : "Open this row from the Memberships queue."}
        onRetry={() => undefined}
        actions={
          <Button variant="secondary" onClick={() => setActionOpen(true)}>
            Status actions
          </Button>
        }
      >
        {row ? (
          <Lines
            items={[
              { label: "Program", value: row.creator_program_id },
              { label: "Profile", value: row.creator_profile_id },
              { label: "Entry reason", value: row.entry_reason ?? "—" },
              { label: "Exit reason", value: row.exit_reason ?? "—" },
            ]}
          />
        ) : null}
      </DetailShell>
      <ReasonApprovalDialog
        open={actionOpen}
        title="Membership action"
        actions={["activate", "hold", "complete", "remove", "expire"]}
        onClose={() => {
          setActionOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.applyMembershipAction(membershipId, input.action as never, {
              ...(input.reason ? { reason: input.reason } : {}),
            });
            toast.push({ title: "Membership updated", tone: "success" });
            setActionOpen(false);
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}

export function CampaignDetailPage() {
  const { campaignId = "" } = useParams();
  const client = useApiClient();
  const api = useMemo(() => createCreatorsApi(client), [client]);
  const toast = useToast();
  const [row, setRow] = useState<CampaignDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionOpen, setActionOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRow((await api.getCampaign(campaignId)).campaign);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [api, campaignId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <DetailShell
        crumb="Campaigns"
        crumbTo="/app/creators/campaigns"
        title={row?.name ?? "Campaign"}
        status={row?.status}
        loading={loading}
        error={error}
        onRetry={() => void load()}
        actions={
          <Button variant="secondary" onClick={() => setActionOpen(true)}>
            Status actions
          </Button>
        }
      >
        {row ? (
          <>
            <Lines
              items={[
                { label: "Program", value: row.creator_program_id ?? "—" },
                { label: "Owner", value: row.owner_user_id ?? "—" },
                { label: "Start", value: row.schedule_start_at ?? "—" },
                { label: "End", value: row.schedule_end_at ?? "—" },
              ]}
            />
            <Flex gap={8}>
              <RelatedLink
                to={`/app/creators/invitations?creator_campaign_id=${row.id}`}
                label="Invitations"
              />
              <RelatedLink
                to={`/app/creators/participations?creator_campaign_id=${row.id}`}
                label="Participations"
              />
            </Flex>
          </>
        ) : null}
      </DetailShell>
      <ReasonApprovalDialog
        open={actionOpen}
        title="Campaign action"
        actions={["submit_approval", "approve", "schedule", "start", "complete", "cancel"]}
        onClose={() => {
          setActionOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.applyCampaignAction(campaignId, input.action as never, {
              ...(input.reason ? { reason: input.reason } : {}),
            });
            toast.push({ title: "Campaign updated", tone: "success" });
            setActionOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}

export function InvitationDetailPage() {
  const { invitationId = "" } = useParams();
  const location = useLocation();
  const client = useApiClient();
  const api = useMemo(() => createCreatorsApi(client), [client]);
  const toast = useToast();
  const [row] = useListBackedRow(
    (location.state as InvitationDto | null)?.id === invitationId
      ? (location.state as InvitationDto)
      : null,
  );
  const [actionOpen, setActionOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [createParticipation, setCreateParticipation] = useState(false);

  return (
    <>
      <DetailShell
        crumb="Invitations"
        crumbTo="/app/creators/invitations"
        title={row?.id ?? invitationId}
        description="Invitation response is not participation."
        status={row?.status}
        loading={false}
        error={row ? null : "Open this row from the Invitations queue."}
        onRetry={() => undefined}
        actions={
          <Button variant="secondary" onClick={() => setActionOpen(true)}>
            Status actions
          </Button>
        }
      >
        {row ? (
          <Lines
            items={[
              { label: "Campaign", value: row.creator_campaign_id },
              { label: "Profile", value: row.creator_profile_id },
              { label: "Sent", value: row.sent_at ?? "—" },
              { label: "Responded", value: row.responded_at ?? "—" },
            ]}
          />
        ) : null}
      </DetailShell>
      <ReasonApprovalDialog
        open={actionOpen}
        title="Invitation action"
        notice="Accept may optionally create pending participation."
        actions={["send", "mark_viewed", "accept", "decline", "expire", "withdraw", "complete"]}
        extraFields={
          <label>
            <input
              type="checkbox"
              checked={createParticipation}
              onChange={(event) => setCreateParticipation(event.target.checked)}
            />{" "}
            Create participation on accept
          </label>
        }
        onClose={() => {
          setActionOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.applyInvitationAction(invitationId, input.action as never, {
              ...(input.reason ? { reason: input.reason } : {}),
              ...(input.action === "accept" && createParticipation
                ? { create_participation: true }
                : {}),
            });
            toast.push({ title: "Invitation updated", tone: "success" });
            setActionOpen(false);
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}

export function ParticipationDetailPage() {
  const { participationId = "" } = useParams();
  const location = useLocation();
  const client = useApiClient();
  const api = useMemo(() => createCreatorsApi(client), [client]);
  const toast = useToast();
  const [row] = useListBackedRow(
    (location.state as ParticipationDto | null)?.id === participationId
      ? (location.state as ParticipationDto)
      : null,
  );
  const [actionOpen, setActionOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  return (
    <>
      <DetailShell
        crumb="Participations"
        crumbTo="/app/creators/participations"
        title={row?.id ?? participationId}
        status={row?.status}
        loading={false}
        error={row ? null : "Open this row from the Participations queue."}
        onRetry={() => undefined}
        actions={
          <Button variant="secondary" onClick={() => setActionOpen(true)}>
            Status actions
          </Button>
        }
      >
        {row ? (
          <Lines
            items={[
              { label: "Campaign", value: row.creator_campaign_id },
              { label: "Profile", value: row.creator_profile_id },
              { label: "Invitation", value: row.creator_invitation_id ?? "—" },
              { label: "Started", value: row.started_at ?? "—" },
              { label: "Completed", value: row.completed_at ?? "—" },
            ]}
          />
        ) : null}
      </DetailShell>
      <ReasonApprovalDialog
        open={actionOpen}
        title="Participation action"
        actions={["activate", "complete", "cancel", "remove"]}
        onClose={() => {
          setActionOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.applyParticipationAction(participationId, input.action as never, {
              ...(input.reason ? { reason: input.reason } : {}),
            });
            toast.push({ title: "Participation updated", tone: "success" });
            setActionOpen(false);
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}

export function BrandDealDetailPage() {
  const { brandDealId = "" } = useParams();
  const client = useApiClient();
  const api = useMemo(() => createCreatorsApi(client), [client]);
  const toast = useToast();
  const [row, setRow] = useState<BrandDealDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionOpen, setActionOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRow((await api.getBrandDeal(brandDealId)).brand_deal);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [api, brandDealId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <DetailShell
        crumb="Brand Deals"
        crumbTo="/app/creators/brand-deals"
        title={row?.id ?? "Brand Deal"}
        description="Lean future-capability status. Not a commercial payment workflow."
        status={row?.status}
        loading={loading}
        error={error}
        onRetry={() => void load()}
        actions={
          <Button variant="secondary" onClick={() => setActionOpen(true)}>
            Status actions
          </Button>
        }
      >
        {row ? (
          <Lines
            items={[
              { label: "Profile", value: row.creator_profile_id },
              { label: "Commercial owner", value: row.commercial_owner_user_id ?? "—" },
              { label: "Starts", value: row.starts_at ?? "—" },
              { label: "Ends", value: row.ends_at ?? "—" },
            ]}
          />
        ) : null}
      </DetailShell>
      <ReasonApprovalDialog
        open={actionOpen}
        title="Brand Deal action"
        actions={["start_review", "approve", "activate", "complete", "cancel", "dispute", "close"]}
        onClose={() => {
          setActionOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.applyBrandDealAction(brandDealId, input.action as never, {
              ...(input.reason ? { reason: input.reason } : {}),
              ...(input.approval_id ? { approval_id: input.approval_id } : {}),
            });
            toast.push({ title: "Brand Deal updated", tone: "success" });
            setActionOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}

export function PerformanceDetailPage() {
  const { recordId = "" } = useParams();
  const location = useLocation();
  const client = useApiClient();
  const api = useMemo(() => createCreatorsApi(client), [client]);
  const toast = useToast();
  const [row] = useListBackedRow(
    (location.state as PerformanceDto | null)?.id === recordId
      ? (location.state as PerformanceDto)
      : null,
  );
  const [actionOpen, setActionOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  return (
    <>
      <DetailShell
        crumb="Performance"
        crumbTo="/app/creators/performance"
        title={row?.id ?? recordId}
        description="Performance is evidence for review, not an independent tier."
        status={row?.status}
        loading={false}
        error={row ? null : "Open this row from the Performance queue."}
        onRetry={() => undefined}
        actions={
          <Button variant="secondary" onClick={() => setActionOpen(true)}>
            Status actions
          </Button>
        }
      >
        {row ? (
          <Lines
            items={[
              { label: "Profile", value: row.creator_profile_id },
              { label: "Campaign", value: row.creator_campaign_id ?? "—" },
              { label: "Period start", value: row.period_start_at },
              { label: "Period end", value: row.period_end_at },
              { label: "Reviewed by", value: row.reviewed_by_user_id ?? "—" },
            ]}
          />
        ) : null}
      </DetailShell>
      <ReasonApprovalDialog
        open={actionOpen}
        title="Performance action"
        actions={["review", "action", "supersede"]}
        onClose={() => {
          setActionOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.applyPerformanceAction(recordId, input.action, {
              ...(input.reason ? { reason: input.reason } : {}),
            });
            toast.push({ title: "Performance updated", tone: "success" });
            setActionOpen(false);
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}

export function PaymentDetailPage() {
  const { paymentId = "" } = useParams();
  const location = useLocation();
  const client = useApiClient();
  const api = useMemo(() => createCreatorsApi(client), [client]);
  const toast = useToast();
  const [row, setRow] = useListBackedRow(
    (location.state as CreatorPaymentDto | null)?.id === paymentId
      ? (location.state as CreatorPaymentDto)
      : null,
  );
  const [actionOpen, setActionOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  return (
    <>
      <DetailShell
        crumb="Creator Payments"
        crumbTo="/app/creators/payments"
        title={row?.id ?? paymentId}
        description="Eligibility/outcome only. Finance owns execution. Linking a payout does not change payout status."
        status={row?.status}
        loading={false}
        error={row ? null : "Open this row from the Creator Payments queue."}
        onRetry={() => undefined}
        actions={
          <Flex gap={8}>
            <Button variant="secondary" onClick={() => setLinkOpen(true)}>
              Link payout
            </Button>
            <Button variant="secondary" onClick={() => setActionOpen(true)}>
              Status actions
            </Button>
          </Flex>
        }
      >
        {row ? (
          <Lines
            items={[
              { label: "Profile", value: row.creator_profile_id },
              { label: "Amount", value: `${row.amount} ${row.currency}` },
              { label: "Payout", value: row.payout_id ?? "—" },
              { label: "Eligibility", value: row.eligibility_reason ?? "—" },
              { label: "Campaign", value: row.creator_campaign_id ?? "—" },
              { label: "Brand Deal", value: row.brand_deal_id ?? "—" },
            ]}
          />
        ) : null}
      </DetailShell>
      <ReasonApprovalDialog
        open={actionOpen}
        title="Creator Payment action"
        notice="These statuses are eligibility/outcome. They do not send money or mutate Finance payouts."
        actions={[
          "submit_review",
          "approve",
          "start_processing",
          "mark_paid",
          "settle",
          "hold",
          "fail",
          "dispute",
          "cancel",
        ]}
        onClose={() => {
          setActionOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.applyPaymentAction(paymentId, input.action as never, {
              ...(input.reason ? { reason: input.reason } : {}),
            });
            toast.push({ title: "Creator Payment updated", tone: "success" });
            setActionOpen(false);
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
      <LinkPayoutDialog
        open={linkOpen}
        busy={busy}
        error={actionError}
        onClose={() => {
          setLinkOpen(false);
          setActionError(null);
        }}
        onSubmit={async (payoutId) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.linkPayout(paymentId, payoutId);
            toast.push({ title: "Payout linked", tone: "success" });
            setLinkOpen(false);
            if (row) setRow({ ...row, payout_id: payoutId });
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}
